import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { navItems, SITE_NAME } from '@/lib/site';
import { btn } from '@/lib/ui';

const SiteHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';
  const overlay = isHome && !scrolled;
  const isActive = (key: string) => {
    if (key === 'home') return location.pathname === '/';
    return location.pathname.startsWith(`/${key}`);
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = query.trim();
    navigate(value ? `/category?q=${encodeURIComponent(value)}` : '/category');
    setQuery('');
  };

  const linkBase =
    'relative text-sm font-label transition-colors duration-200';

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        overlay
          ? 'bg-transparent'
          : 'border-b border-background-200 bg-background-50/95 backdrop-blur'
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center justify-between px-4 md:h-20 md:px-8">
        <Link to="/" className="flex items-center gap-2.5" aria-label={`${SITE_NAME}首页`}>
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-play-circle-fill text-xl" />
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-lg font-semibold tracking-tight ${
                overlay ? 'text-background-50' : 'text-foreground-950'
              }`}
            >
              {SITE_NAME}
            </span>
            <span
              className={`mt-0.5 text-[10px] font-label tracking-[0.18em] ${
                overlay ? 'text-background-200' : 'text-foreground-500'
              }`}
            >
              KANLIAO
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="主导航">
          {navItems.map((item) => (
            <Link
              key={item.key}
              to={item.to}
              className={`${linkBase} ${
                overlay
                  ? isActive(item.key)
                    ? 'text-accent-300'
                    : 'text-background-100 hover:text-accent-300'
                  : isActive(item.key)
                    ? 'text-primary-600'
                    : 'text-foreground-700 hover:text-primary-600'
              }`}
            >
              {item.label}
              {isActive(item.key) && (
                <span
                  className={`absolute -bottom-1.5 left-0 h-px w-full ${
                    overlay ? 'bg-accent-300' : 'bg-primary-500'
                  }`}
                />
              )}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <form onSubmit={submitSearch} className="relative hidden lg:block" role="search">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center">
              <i className={`ri-search-line text-sm ${overlay ? 'text-background-200' : 'text-foreground-400'}`} />
            </span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索热料、人物或话题"
              aria-label="搜索内容"
              className={`w-56 rounded-md border py-2 pl-9 pr-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400 ${
                overlay
                  ? 'border-background-50/30 bg-background-50/10 text-background-50 placeholder:text-background-200'
                  : 'border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400'
              }`}
            />
          </form>
          <Link to="/category" className={btn('primary', 'sm')}>
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-play-fill text-sm" />
            </span>
            开始观看
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label={menuOpen ? '关闭菜单' : '打开菜单'}
          aria-expanded={menuOpen}
          className={`flex h-10 w-10 items-center justify-center rounded-md md:hidden ${
            overlay ? 'text-background-50' : 'text-foreground-800'
          }`}
        >
          <i className={menuOpen ? 'ri-close-line text-2xl' : 'ri-menu-2-line text-2xl'} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-6 pt-4 md:hidden">
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                className={`rounded-md px-3 py-3 text-sm font-label transition-colors ${
                  isActive(item.key)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <form onSubmit={submitSearch} className="relative mt-4" role="search">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center">
              <i className="ri-search-line text-sm text-foreground-400" />
            </span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索热料、人物或话题"
              aria-label="搜索内容"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </form>
          <Link to="/category" className={`${btn('primary', 'md')} mt-4 w-full`}>
            开始观看
          </Link>
        </div>
      )}
    </header>
  );
};

export default SiteHeader;